/**
 * Route safety check — look a planned route over before we rely on it, and flag
 * any leg that ploughs across charted land or passes over water shallower than
 * our safety depth. It answers the planning-desk question "is this route actually
 * floatable?" before we ever cast off.
 *
 * This is deliberately pure geometry plus callbacks: it takes the waypoint
 * positions, the safety depth, a function that hands back the land polygons near
 * a leg, and a function that samples charted depth at a point. No store, map, or
 * chart-index imports, so it unit-tests cleanly and can't drag half the app into
 * a test. The caller (RouteSheet / MapView) wires the callbacks to the live land
 * index and the same depth sampler the grounding alarm already uses.
 *
 * Safety bias: when in doubt we warn. A point with no depth data is "unknown"
 * (a caution), never silently "safe"; a leg outside loaded chart coverage is
 * reported as such rather than waved through. Better a false warning than a
 * missed hazard.
 */

import { assessTideGate, type TideGateResult, type TideGateWindow } from './tideGate';

/** A position in [lon, lat] degrees, matching the rest of the geometry code. */
export type LngLat = [number, number];

/** Sensible fallback when the user hasn't set a safety depth yet, metres. */
export const DEFAULT_SAFETY_DEPTH_M = 2;

/** Default sampling step along a leg, nm (~37 m). Dense enough for a thin spit. */
export const DEFAULT_STEP_NM = 0.02;

/** Cap on samples per leg, so a very long ocean leg still checks in a blink. */
export const DEFAULT_MAX_SAMPLES_PER_LEG = 600;

export interface RouteSafetyInput {
  /** Ordered waypoint positions, [lon, lat]. Legs run between consecutive pairs. */
  waypoints: LngLat[];
  /**
   * The depth at/above which we want water flagged, metres (chart datum). If
   * this is null/undefined or not a positive number we fall back to
   * DEFAULT_SAFETY_DEPTH_M and say so in the result.
   */
  safetyDepthM: number | null | undefined;
  /**
   * Candidate land rings (outer boundaries, [lon, lat] vertices) overlapping a
   * leg's neighbourhood. Same LNDARE land source the AIS-vector clip uses; the
   * caller pulls these from the chart spatial index for the leg's bbox.
   */
  landRingsForLeg: (from: LngLat, to: LngLat) => LngLat[][];
  /** Charted depth under a point, metres, or null when there's no depth data. */
  depthAt: (pos: LngLat) => number | null;
  /**
   * Optional: whether a point falls inside loaded chart coverage. When supplied,
   * points outside coverage are reported as "outside chart coverage" rather than
   * judged. When omitted we lean on depthAt returning null (unknown) instead.
   */
  inCoverage?: (pos: LngLat) => boolean;
  /** Sampling step along each leg, nm. Defaults to DEFAULT_STEP_NM. */
  stepNm?: number;
  /** Max samples per leg. Defaults to DEFAULT_MAX_SAMPLES_PER_LEG. */
  maxSamplesPerLeg?: number;
  /**
   * Height of tide above chart datum at a point and time, metres, or null when
   * there's no height data for that spot. When supplied together with
   * `assessFromMs`, a shallow leg is re-judged with the tide on top of the
   * charted depth: it may turn out passable within a tidal gate, a genuine hazard
   * (too shallow even at high water), or "needs a manual tide calc" where there's
   * no height data. Omit it and a shallow leg stays a plain charted-shallow
   * hazard, exactly as before.
   */
  tideHeightAt?: (pos: LngLat, ms: number) => number | null;
  /**
   * Start of the tidal-gate search window, epoch ms (the plan/scrubber time on
   * the tide model's own timeline). Required alongside `tideHeightAt` for the
   * tide assessment to run.
   */
  assessFromMs?: number;
  /** Tidal-gate search window length, hours. Defaults to the helper's default. */
  assessWindowHours?: number;
}

export interface LegFinding {
  /** 0-based leg index: the leg from waypoints[legIndex] to waypoints[legIndex+1]. */
  legIndex: number;
  /** The leg segment intersects, or lies within, a charted land polygon. */
  crossesLand: boolean;
  /** Some sampled point is shallower than the safety depth (confirmed shallow). */
  shallow: boolean;
  /** Shallowest depth found on the leg at/below the safety depth, metres, else null. */
  minDepthM: number | null;
  /** A sampled point had no depth data — treat as caution, not safe. */
  unknownDepth: boolean;
  /** Part or all of the leg falls outside loaded chart coverage. */
  outsideCoverage: boolean;
  /** The worst hazard point on the leg, [lon, lat]: land crossing first, else shallowest. */
  worstPointLatLng?: LngLat;
  /** Leg length, nm. */
  lengthNm: number;
  /**
   * The tide assessment for a shallow leg, when a tide-height fn and window were
   * supplied. Undefined when the leg isn't shallow, or when no tide data was
   * given (in which case a shallow leg is treated as a plain hazard as before).
   */
  tideGate?: TideGateResult;
  /**
   * The single verdict for the leg, folding land, tide and coverage together:
   *   - 'hazard'      crosses land, or shallow and too shallow even at high water.
   *   - 'gated'       shallow but passable within a tidal gate (see tideGate).
   *   - 'manual-tide' shallow and no tide-height data, so a manual calc is needed.
   *   - 'caution'     unknown depth or outside chart coverage, nothing confirmed.
   *   - 'clear'       nothing flagged.
   */
  category: LegCategory;
  /** True when nothing was flagged: no land, no shallow, no unknown, in coverage. */
  clear: boolean;
}

export type LegCategory = 'hazard' | 'gated' | 'manual-tide' | 'caution' | 'clear';

export interface RouteSafetyResult {
  legs: LegFinding[];
  /** Legs with a confirmed hazard (land crossing, or shallow even at high water). */
  hazardCount: number;
  /** Shallow legs that are passable within a tidal gate (not blocking, mind timing). */
  gatedCount: number;
  /** Shallow legs with no tide-height data, needing a manual tidal calculation. */
  manualTideCount: number;
  /** Legs with a caution only (unknown depth or outside coverage), no confirmed hazard. */
  cautionCount: number;
  /** Legs that are fully clear. */
  clearCount: number;
  /** The safety depth actually used, metres. */
  safetyDepthM: number;
  /** True when we had to fall back to the default because none was set. */
  safetyDepthDefaulted: boolean;
}

/** Rough great-circle-ish distance for sample counting, nm. Equirectangular is plenty here. */
function approxNm(a: LngLat, b: LngLat): number {
  const dLatNm = (b[1] - a[1]) * 60;
  const midLatRad = (((a[1] + b[1]) / 2) * Math.PI) / 180;
  const dLonNm = (b[0] - a[0]) * 60 * Math.cos(midLatRad);
  return Math.hypot(dLatNm, dLonNm);
}

/** Linear interpolation along a leg in lon/lat; fine at leg scale. */
function lerp(a: LngLat, b: LngLat, t: number): LngLat {
  return [a[0] + t * (b[0] - a[0]), a[1] + t * (b[1] - a[1])];
}

/** Even-odd ray-cast point-in-polygon for a single ring. */
function pointInRing(pt: LngLat, ring: LngLat[]): boolean {
  let inside = false;
  const x = pt[0];
  const y = pt[1];
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0];
    const yi = ring[i][1];
    const xj = ring[j][0];
    const yj = ring[j][1];
    const hit = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (hit) inside = !inside;
  }
  return inside;
}

function pointInAnyRing(pt: LngLat, rings: LngLat[][]): boolean {
  for (const ring of rings) if (pointInRing(pt, ring)) return true;
  return false;
}

/**
 * Parameter t (0 at a, 1 at b) of the first point where segment a->b crosses any
 * ring edge, or null if it never does. Walks out from a so we report where the
 * leg first meets land.
 */
function firstLandCrossingT(a: LngLat, b: LngLat, rings: LngLat[][]): number | null {
  const rx = b[0] - a[0];
  const ry = b[1] - a[1];
  const minX = Math.min(a[0], b[0]);
  const maxX = Math.max(a[0], b[0]);
  const minY = Math.min(a[1], b[1]);
  const maxY = Math.max(a[1], b[1]);
  let bestT: number | null = null;
  for (const ring of rings) {
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const c = ring[j];
      const d = ring[i];
      // Cheap reject: edge wholly to one side of the segment's box can't cross it.
      if (Math.max(c[0], d[0]) < minX || Math.min(c[0], d[0]) > maxX) continue;
      if (Math.max(c[1], d[1]) < minY || Math.min(c[1], d[1]) > maxY) continue;
      const sx = d[0] - c[0];
      const sy = d[1] - c[1];
      const denom = rx * sy - ry * sx;
      if (denom === 0) continue; // parallel or degenerate
      const qpx = c[0] - a[0];
      const qpy = c[1] - a[1];
      const t = (qpx * sy - qpy * sx) / denom;
      const u = (qpx * ry - qpy * rx) / denom;
      if (t < 0 || t > 1 || u < 0 || u > 1) continue;
      if (bestT === null || t < bestT) bestT = t;
    }
  }
  return bestT;
}

function normaliseSafetyDepth(v: number | null | undefined): { depth: number; defaulted: boolean } {
  if (typeof v === 'number' && Number.isFinite(v) && v > 0) return { depth: v, defaulted: false };
  return { depth: DEFAULT_SAFETY_DEPTH_M, defaulted: true };
}

/**
 * Check every leg of a route. Returns a per-leg finding plus roll-up counts.
 * A route with fewer than two waypoints has no legs, so we return an empty,
 * all-clear result.
 */
export function checkRouteSafety(input: RouteSafetyInput): RouteSafetyResult {
  const { waypoints, landRingsForLeg, depthAt, inCoverage, tideHeightAt, assessFromMs, assessWindowHours } =
    input;
  const stepNm = input.stepNm && input.stepNm > 0 ? input.stepNm : DEFAULT_STEP_NM;
  const maxSamples =
    input.maxSamplesPerLeg && input.maxSamplesPerLeg > 0
      ? Math.floor(input.maxSamplesPerLeg)
      : DEFAULT_MAX_SAMPLES_PER_LEG;
  const { depth: safetyDepthM, defaulted: safetyDepthDefaulted } = normaliseSafetyDepth(
    input.safetyDepthM,
  );

  const legs: LegFinding[] = [];
  for (let i = 0; i + 1 < waypoints.length; i++) {
    const from = waypoints[i];
    const to = waypoints[i + 1];
    const lengthNm = approxNm(from, to);

    // Land: pull candidate rings once for the whole leg. First test the segment
    // against the ring edges (catches a leg slicing across a headland or a thin
    // island, entry and exit), then also test sample points inside a polygon
    // (catches an endpoint on land, or a leg lying wholly within a big polygon).
    const rings = landRingsForLeg(from, to);
    let crossesLand = false;
    let landPoint: LngLat | undefined;
    if (rings.length) {
      const tCross = firstLandCrossingT(from, to, rings);
      if (tCross !== null) {
        crossesLand = true;
        landPoint = lerp(from, to, tCross);
      } else if (pointInAnyRing(from, rings)) {
        crossesLand = true;
        landPoint = from;
      } else if (pointInAnyRing(to, rings)) {
        crossesLand = true;
        landPoint = to;
      }
    }

    // Depth + coverage: step along the leg. Include both endpoints. Cap the count
    // so a long leg stays fast; land detection above is independent of this cap,
    // so widening the depth step never hides a coastline.
    const n = Math.max(1, Math.min(maxSamples, Math.ceil(lengthNm / stepNm)));
    let shallow = false;
    let minDepthM: number | null = null;
    let shallowPoint: LngLat | undefined;
    let unknownDepth = false;
    let unknownPoint: LngLat | undefined;
    let outsideCoverage = false;
    // Charted depths at the sub-safety-depth samples, for the tide assessment. The
    // shallowest of these governs whether the tide can lift the leg over the bar.
    const shallowDepths: number[] = [];

    for (let s = 0; s <= n; s++) {
      const p = lerp(from, to, s / n);
      if (inCoverage && !inCoverage(p)) {
        outsideCoverage = true;
        continue; // no charts here: don't judge it safe, don't judge it shallow
      }
      const d = depthAt(p);
      if (d == null || !Number.isFinite(d)) {
        unknownDepth = true;
        if (!unknownPoint) unknownPoint = p;
        continue;
      }
      if (d < safetyDepthM) {
        shallow = true;
        shallowDepths.push(d);
        if (minDepthM == null || d < minDepthM) {
          minDepthM = d;
          shallowPoint = p;
        }
      }
    }

    // Tide assessment: a shallow leg isn't automatically a hazard. If we've been
    // given a height-of-tide fn and a window, sample the tide over the shallowest
    // spot and see whether it lifts the leg over the safety depth, and when. That
    // turns a bare "shallow" into a tidal gate, a real hazard, or a manual-calc
    // note where there's no height data.
    let tideGate: TideGateResult | undefined;
    if (shallow && tideHeightAt && assessFromMs != null && shallowPoint) {
      const gatePoint = shallowPoint;
      tideGate = assessTideGate({
        chartedDepthsM: shallowDepths,
        tideHeightAt: (ms) => tideHeightAt(gatePoint, ms),
        safetyDepthM,
        windowStartMs: assessFromMs,
        windowHours: assessWindowHours,
      });
    }

    // Land is the worst thing that can happen to a leg, so it wins the "worst
    // point"; then confirmed-shallow water; then, if nothing confirmed, the first
    // unknown point so the crew has somewhere to look.
    const worstPointLatLng = crossesLand
      ? landPoint
      : shallow
        ? shallowPoint
        : unknownDepth
          ? unknownPoint
          : undefined;

    const clear = !crossesLand && !shallow && !unknownDepth && !outsideCoverage;

    // Fold everything into one verdict. Land is always a hazard. A shallow leg's
    // verdict comes from the tide assessment when we have one: passable-with-tide
    // -> gated, never-clears -> hazard, no-height-data -> manual-tide. With no
    // tide assessment a shallow leg stays a hazard (the old behaviour). Cautions
    // (unknown depth / no coverage) only stand when nothing worse is present.
    let category: LegCategory;
    if (crossesLand) {
      category = 'hazard';
    } else if (shallow) {
      if (tideGate?.verdict === 'passable-with-tide') category = 'gated';
      else if (tideGate?.verdict === 'needs-manual-calc') category = 'manual-tide';
      else category = 'hazard'; // 'hazard', 'clear' (shouldn't co-occur), or no assessment
    } else if (unknownDepth || outsideCoverage) {
      category = 'caution';
    } else {
      category = 'clear';
    }

    legs.push({
      legIndex: i,
      crossesLand,
      shallow,
      minDepthM,
      unknownDepth,
      outsideCoverage,
      worstPointLatLng,
      lengthNm,
      tideGate,
      category,
      clear,
    });
  }

  let hazardCount = 0;
  let gatedCount = 0;
  let manualTideCount = 0;
  let cautionCount = 0;
  let clearCount = 0;
  for (const leg of legs) {
    switch (leg.category) {
      case 'hazard':
        hazardCount++;
        break;
      case 'gated':
        gatedCount++;
        break;
      case 'manual-tide':
        manualTideCount++;
        break;
      case 'caution':
        cautionCount++;
        break;
      default:
        clearCount++;
    }
  }

  return {
    legs,
    hazardCount,
    gatedCount,
    manualTideCount,
    cautionCount,
    clearCount,
    safetyDepthM,
    safetyDepthDefaulted,
  };
}

// -----------------------------------------------------------------------------
// Presentation helper — turn a result into plain-English lines for the sheet.
// Kept here (pure) so the wording is unit-tested alongside the geometry.
// -----------------------------------------------------------------------------

export interface RouteSafetySummary {
  /** One-line verdict for the top of the panel. */
  headline: string;
  /** Confirmed problems (land, or shallow even at high water), one line per leg. */
  hazards: string[];
  /** Shallow legs passable within a tidal gate, with the crossing window(s). */
  gates: string[];
  /** Shallow legs with no tide data, needing a manual tidal calculation. */
  manualTide: string[];
  /** Softer warnings (unknown depth, outside coverage), one line per leg. */
  cautions: string[];
  /** Set when we had to assume a safety depth because none was configured. */
  safetyNote: string | null;
}

export interface SummariseOpts {
  /**
   * Format a tidal-gate time (epoch ms) for display, e.g. local "08:15". Defaults
   * to a UTC HH:MM so the summary is self-contained and testable; the UI passes a
   * formatter in the user's chosen zone.
   */
  formatTime?: (ms: number) => string;
}

/** 1-based leg label, e.g. leg index 2 -> "Leg 3". */
function legLabel(legIndex: number): string {
  return `Leg ${legIndex + 1}`;
}

/** UTC HH:MM fallback so the summary reads sensibly with no formatter injected. */
function defaultFormatTime(ms: number): string {
  const d = new Date(ms);
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

/** One or more gate windows -> "08:15–11:40" (or "08:15 onwards" when open-ended). */
function describeWindows(windows: TideGateWindow[], fmt: (ms: number) => string): string {
  return windows
    .map((w) => (w.openEnded ? `${fmt(w.startMs)} onwards` : `${fmt(w.startMs)}–${fmt(w.endMs)}`))
    .join(', ');
}

/**
 * Plain-English summary of a route check. Confirmed hazards (land, shallow even
 * at high water) are separated from the softer outcomes so the crew can see at a
 * glance what's blocking, what's a timing job (a tidal gate), what needs a manual
 * tide calc, and what just needs a look.
 */
export function summariseRouteSafety(result: RouteSafetyResult, opts?: SummariseOpts): RouteSafetySummary {
  const fmt = opts?.formatTime ?? defaultFormatTime;
  const hazards: string[] = [];
  const gates: string[] = [];
  const manualTide: string[] = [];
  const cautions: string[] = [];
  const depthStr = result.safetyDepthM.toFixed(1);
  const depthPhrase = result.safetyDepthDefaulted
    ? `the default ${depthStr} m safety depth`
    : `your ${depthStr} m safety depth`;

  for (const leg of result.legs) {
    const label = legLabel(leg.legIndex);
    const minStr = leg.minDepthM != null ? `${leg.minDepthM.toFixed(1)} m` : 'shallow';

    // A land crossing is always a hazard, and it can sit on a shallow leg too, so
    // it's handled first and takes the hazard line.
    if (leg.crossesLand) {
      const extra =
        leg.shallow && leg.minDepthM != null ? `, and shallow water (${minStr}, below ${depthPhrase})` : '';
      hazards.push(`${label} crosses land${extra}.`);
      continue;
    }

    switch (leg.category) {
      case 'gated': {
        const windows = leg.tideGate?.windows ?? [];
        const when = windows.length ? describeWindows(windows, fmt) : 'part of the tide';
        gates.push(
          `${label} shallow (${minStr} charted, below ${depthPhrase}) but passable ${when} with the tide on top (tidal gate). Only cross inside that window.`,
        );
        break;
      }
      case 'manual-tide': {
        manualTide.push(
          `${label} shallow (${minStr} charted, below ${depthPhrase}) with no tide-height data here, so a tidal height calculation will need to be undertaken.`,
        );
        break;
      }
      case 'hazard': {
        // Shallow even at high water: say how far short the top of the tide falls,
        // when we worked it out, so it's clearly a genuine hazard not a gate.
        const best = leg.tideGate?.maxEffectiveDepthM;
        const tail =
          best != null
            ? `, and stays below ${depthPhrase} even at high water (best it reaches is ${best.toFixed(1)} m)`
            : `, below ${depthPhrase}`;
        hazards.push(`${label} shallow water (${minStr} charted${tail}). Too shallow to rely on.`);
        break;
      }
      case 'caution': {
        if (leg.outsideCoverage) cautions.push(`${label} runs outside the charts we have loaded.`);
        else if (leg.unknownDepth) cautions.push(`${label} has no charted depth for part of it.`);
        break;
      }
      default:
        break; // clear
    }
  }

  let headline: string;
  const n = (x: number) => (x === 1 ? 'leg' : 'legs');
  if (result.hazardCount > 0) {
    const c = result.hazardCount;
    headline = `${c} ${c === 1 ? 'leg needs' : 'legs need'} a look before you rely on this route.`;
  } else if (result.gatedCount > 0) {
    const c = result.gatedCount;
    headline = `${c} shallow ${n(c)} passable within a tidal gate — mind the timing.`;
  } else if (result.manualTideCount > 0) {
    const c = result.manualTideCount;
    headline = `${c} shallow ${n(c)} need a manual tide calculation before you rely on this route.`;
  } else if (result.cautionCount > 0) {
    const c = result.cautionCount;
    headline = `No confirmed hazards, but ${c} ${n(c)} we couldn't fully check.`;
  } else {
    const c = result.legs.length;
    headline = `No hazards found on ${c} ${n(c)}.`;
  }

  const safetyNote = result.safetyDepthDefaulted
    ? `No safety depth set, so we used a default of ${depthStr} m. Set your own in Settings for a check that matches your boat.`
    : null;

  return { headline, hazards, gates, manualTide, cautions, safetyNote };
}

/**
 * The chart-highlight severity for a leg, or null when it's clear. Centralised
 * here so the map colours match the summary wording: a confirmed hazard is red,
 * a tidal gate its own colour, a manual-tide leg another, a caution amber.
 */
export type LegSeverity = 'hazard' | 'gate' | 'manual' | 'caution';
export function legHighlightSeverity(leg: LegFinding): LegSeverity | null {
  switch (leg.category) {
    case 'hazard':
      return 'hazard';
    case 'gated':
      return 'gate';
    case 'manual-tide':
      return 'manual';
    case 'caution':
      return 'caution';
    default:
      return null;
  }
}
